import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, computed, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  Router,
  NavigationEnd,
  RouterLinkActive,
  RouterLink,
} from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { RippleModule } from 'primeng/ripple';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { AuthService } from 'src/app/core/services/auth.service';
import { NAVIGATION_ITEMS, NavigationItem } from './navigation-items';
import { MenuItem } from 'primeng/api';
import { Role } from 'src/app/core/enums/role.enum';
import {
  LOGIN_METHOD_KEY,
  LoginMethod,
} from 'src/app/core/enums/login-method.enum';

@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    animations: [
        trigger('expandList', [
            state('hidden', style({
                height: '0',
            })),
            state('visible', style({
                height: '*',
            })),
            transition('visible <=> hidden', [
                animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'),
            ]),
            transition('void => *', animate(0)),
        ]),
    ],
    imports: [
        CommonModule,
        StyleClassModule,
        RippleModule,
        RouterLinkActive,
        RouterLink,
    ]
})
export class NavigationComponent implements OnDestroy {
  private _identityUserSubscription!: Subscription;

  navigationEnd = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    )
  );

  navigationItems = computed(() => {
    const navigationItems = NAVIGATION_ITEMS;
    navigationItems.forEach((n) => n.routerLink);
    this.showNavigationItemsBasedOnPermissions(navigationItems);
    this.updateNavigationMenu(navigationItems);
    return navigationItems;
  });

  expandedItem = signal<Nullable<NavigationItem>>(null);

  constructor(
    private router: Router,
    public authService: AuthService,
  ) {}

  ngOnDestroy(): void {
    this._identityUserSubscription?.unsubscribe();
  }

  toggleItem(item: NavigationItem) {
    item.expanded = !item.expanded;
    this.expandedItem.set(item.expanded ? item : null);

    if (item.hasActiveRouterLink && !item.expanded) return;
  }

  updateNavigationMenu(navigationItems: NavigationItem[]) {
    navigationItems
      .filter((i) => i.expandable && i.items)
      .forEach((i) => {
        if (
          !i.items?.find((child) =>
            this.router.isActive(child.routerLink, {
              paths: 'exact',
              matrixParams: 'exact',
              queryParams: 'subset',
              fragment: 'ignored',
            })
          )
        ) {
          i.expanded = false;
          i.hasActiveRouterLink = false;
          i.styleClass = 'bg-transparent';
          return;
        }

        i.expanded = true;
        i.hasActiveRouterLink = true;
        i.styleClass = 'surface-hover';
      });
  }

  showNavigationItemsBasedOnPermissions(
    ...navigationGroups: NavigationItem[][]
  ) {
    const flattenItems = navigationGroups.flatMap((g) => g);

    for (const item of flattenItems) {
      if (item.items) {
        this.showNavigationItemsBasedOnPermissions(item.items);
        item.visible = !!item.items.find((i: MenuItem) => i.visible);
        continue;
      }

      const isAuthorized = this.userRole();
      item.visible = !item.roles || isAuthorized;
    }
  }

  userRole(): boolean {
    const loginMethod = localStorage.getItem(LOGIN_METHOD_KEY);
    let hasAdmin = false;

    if (loginMethod === LoginMethod.IAM) {
      const roles = this.authService.user()?.role;
      hasAdmin = roles?.includes(Role.Administrator) || false;
    } else if (loginMethod === LoginMethod.Identity) {
      hasAdmin =
        this.authService.identityUser()?.roles.includes(Role.Administrator) ||
        false;
    }

    return hasAdmin;
  }
}
